import type { OutreachBulkSend } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';

/** "Drafting 5 of 8" while it runs, "Drafts ready, review" once it is over; failed creators are named in both, and a batch where all failed says so. */
export function bulkSendLine(send: OutreachBulkSend): string {
  if (send.replaced && !send.running) return 'Replaced by a newer draft';
  const failed = send.failed > 0 ? ` · ${send.failed} failed` : '';
  if (send.running) return `Drafting ${send.generated + send.skipped + send.failed} of ${send.total}${failed}`;
  if (send.total > 0 && send.failed === send.total) return `${send.failed} failed, open to retry`;
  return `Drafts ready, review${failed}`;
}

export function bulkSendsTitle(sends: OutreachBulkSend[]): string {
  const running = sends.filter((send) => send.running).length;
  // A send whose drafts a newer draft replaced has nothing to review: only live ones count as ready.
  return running > 0 ? `Bulk sends: ${running} running` : `Bulk sends: ${sends.filter((send) => !send.replaced).length} ready`;
}

function startedAt(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** The header title that lists every Bulk send not yet sent, for all admins. Hidden when there are none. */
export function BulkSendsMenu({ sends, onOpen }: { sends: OutreachBulkSend[]; onOpen: (batchId: string) => void }) {
  if (sends.length === 0) return null;
  const title = bulkSendsTitle(sends);
  const anyRunning = sends.some((send) => send.running);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" aria-label={title}>
          {anyRunning ? <span aria-hidden="true" className="size-2 animate-pulse rounded-full bg-(--brand-500) motion-reduce:animate-none" /> : null}
          {title}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <ul className="flex flex-col gap-1">
          {sends.map((send) => (
            <li key={send.batchId}>
              <button
                type="button"
                disabled={send.replaced && !send.running}
                className="flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-2 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-hidden disabled:opacity-60 disabled:hover:bg-transparent"
                onClick={() => onOpen(send.batchId)}
              >
                <span className="text-body-sm-medium text-foreground">{send.templateName ?? 'Bulk send'}</span>
                <span className="text-body-sm-regular text-foreground">{bulkSendLine(send)}</span>
                <span className="text-body-sm-regular text-muted-foreground">{`${send.startedBy} · ${startedAt(send.startedAt)}`}</span>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
